/*
  Find Your Team — "Open team drawer" mode.

  Wraps the section's <fancy-select no-redirect> (link mode) and, on pick, opens a
  right-hand drawer of that league's teams instead of navigating to the league page.
  Teams are fetched once from the Storefront API when the section scrolls into view
  and cached for the session.
*/

const FYT_CACHE_KEY = 'fyt-team-directory-v1';

const FYT_TEAMS_GQL = `query FindYourTeamTeams($cursor: String) {
  metaobjects(type: "sparky--team", first: 250, after: $cursor) {
    pageInfo { hasNextPage endCursor }
    nodes {
      handle
      name: field(key: "name") { value }
      override: field(key: "override") { value }
      url: field(key: "url") { value }
      leagues: field(key: "leagues") { value }
      logo: field(key: "logo") {
        reference { ... on MediaImage { image { url(transform: { maxWidth: 48, maxHeight: 48 }) } } }
      }
    }
  }
}`;

const toGid = id => (id && !String(id).startsWith('gid://') ? `gid://shopify/Metaobject/${id}` : id);

const escapeHtml = str => String(str ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Mirrors snippets/team-name.liquid: dashes → spaces, lowercased (capitalized via CSS),
// with the optional override kept in its original casing.
function fytTeamNameHtml(rawName, override) {
  const base = escapeHtml((rawName || '').replace(/-/g, ' ').toLowerCase());
  if (!override) return base;
  const safeOverride = escapeHtml(override);
  const idx = base.indexOf(safeOverride.toLowerCase());
  if (idx === -1) return base;
  return `${base.slice(0, idx)}<span style="text-transform:none">${safeOverride}</span>${base.slice(idx + safeOverride.length)}`;
}

// Team `url` values are absolute production URLs — keep links on the current domain.
function fytTeamHref(url, handle) {
  if (!url) return `/collections/${handle}`;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname.endsWith('47brand.com') ? `${parsed.pathname}${parsed.search}` : parsed.href;
  } catch {
    return `/collections/${handle}`;
  }
}

function fytParseList(value) {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

let fytDirectoryPromise = null;

// Resolves to [{ handle, name, override, href, logo, leagues: [gid] }], sorted A–Z.
function fytLoadTeams(shop, token) {
  if (fytDirectoryPromise) return fytDirectoryPromise;

  try {
    const cached = JSON.parse(sessionStorage.getItem(FYT_CACHE_KEY) || 'null');
    if (Array.isArray(cached) && cached.length) return (fytDirectoryPromise = Promise.resolve(cached));
  } catch {}

  fytDirectoryPromise = (async () => {
    const nodes = [];
    let cursor = null;
    let page = 0;
    do {
      const res = await fetch(`https://${shop}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: FYT_TEAMS_GQL, variables: { cursor } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data, errors } = await res.json();
      if (errors?.length) throw new Error(errors[0].message);
      const connection = data?.metaobjects;
      nodes.push(...(connection?.nodes ?? []));
      cursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
      page++;
    } while (cursor && page < 20);

    const teams = nodes
      .map(node => ({
        handle: node.handle,
        name: node.name?.value || node.handle,
        override: node.override?.value || '',
        href: fytTeamHref(node.url?.value, node.handle),
        logo: node.logo?.reference?.image?.url ?? '',
        leagues: fytParseList(node.leagues?.value),
      }))
      .sort((a, b) => a.name.replace(/-/g, ' ').localeCompare(b.name.replace(/-/g, ' ')));

    try { sessionStorage.setItem(FYT_CACHE_KEY, JSON.stringify(teams)); } catch {}
    return teams;
  })().catch(err => {
    fytDirectoryPromise = null;
    throw err;
  });

  return fytDirectoryPromise;
}

if (!customElements.get('find-your-team')) {
  customElements.define('find-your-team', class FindYourTeam extends HTMLElement {
    connectedCallback() {
      this.drawerId  = this.dataset.drawerId;
      this.template  = this.querySelector(`template[data-modal-content="${this.drawerId}"]`);
      this.select    = this.querySelector('fancy-select');
      this.leagues   = [];
      try { this.leagues = JSON.parse(this.querySelector('[data-fyt-leagues]')?.textContent || '[]'); } catch {}
      this.activeLeague = null;
      this.teams = null;
      this.error = false;

      // fancy-select[no-redirect] emits 'change' with the picked link's href instead of navigating.
      this.onChange = e => {
        if (e.target === this.select && e.detail?.value) this.pick(e.detail.value);
      };
      this.addEventListener('change', this.onChange);

      this.onSearch = e => {
        if (!e.target.matches('[data-fyt-drawer-search]')) return;
        const drawer = e.target.closest(`[data-modal-content="${this.drawerId}"]`);
        if (drawer) this.filter(drawer, e.target.value);
      };
      document.addEventListener('input', this.onSearch);

      this.onModalClosed = e => {
        if (e.detail !== this.drawerId) return;
        this.select?.querySelector('.fs-trigger')?.focus({ preventScroll: true });
      };
      window.addEventListener('modal:closed', this.onModalClosed);

      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this.closest('[data-fyt-section]') ?? this);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('input', this.onSearch);
      window.removeEventListener('modal:closed', this.onModalClosed);
      this.observer?.disconnect();
    }

    load() {
      if (this.loading) return this.loading;
      this.loading = fytLoadTeams(this.dataset.shop, this.dataset.token)
        .then(teams => { this.teams = teams; this.error = false; })
        .catch(err => {
          this.error = true;
          this.loading = null;
          console.error('[find-your-team] fetch error:', err);
        })
        .finally(() => this.renderLive());
      return this.loading;
    }

    pick(href) {
      const league = this.leagues.find(l => l.href === href);
      if (!league) { window.location = href; return; }

      this.activeLeague = league;
      this.renderInto(this.template.content);
      this.template.dataset.modalLabel = this.title(league);
      window.dispatchEvent(new CustomEvent('modal:openById', { detail: this.drawerId }));
      if (!this.teams) this.load();
    }

    title(league) {
      return (this.dataset.titleFormat || '__LEAGUE__ Teams').replace('__LEAGUE__', league.name);
    }

    liveDrawer() {
      return document.querySelector(`[data-modal-content="${this.drawerId}"]:not(template)`);
    }

    renderLive() {
      const drawer = this.liveDrawer();
      if (drawer && this.activeLeague) this.renderInto(drawer);
    }

    renderInto(root) {
      const league = this.activeLeague;
      const logo  = root.querySelector('[data-fyt-drawer-logo]');
      const title = root.querySelector('[data-fyt-drawer-title]');
      const list  = root.querySelector('[data-fyt-drawer-list]');
      const search = root.querySelector('[data-fyt-drawer-search]');

      if (logo) {
        logo.hidden = !league.logo;
        if (league.logo) logo.setAttribute('src', league.logo);
        else logo.removeAttribute('src');
      }
      if (title) title.textContent = this.title(league);
      if (!list) return;

      const status = text => `<li class="py-4 text-center text-sm text-dove-grey" data-fyt-drawer-status>${escapeHtml(text)}</li>`;

      if (this.error) {
        list.innerHTML = status(this.dataset.i18nError);
        return;
      }
      if (!this.teams) {
        list.innerHTML = status(this.dataset.i18nLoading);
        return;
      }

      const leagueGid = toGid(league.id);
      const teams = this.teams.filter(team => team.leagues.includes(leagueGid));
      if (!teams.length) {
        list.innerHTML = status(this.dataset.i18nNoTeams);
        return;
      }

      list.innerHTML = teams.map(team => `
        <li data-fyt-team data-team-name="${escapeHtml(team.name.replace(/-/g, ' ').toLowerCase())}" data-team-handle="${escapeHtml(team.handle)}">
          <a
            href="${escapeHtml(team.href)}"
            class="flex items-center w-full py-3.5 border-b border-silver text-sm text-primary-dark hover:text-dove-grey transition-all ease-in"
          >
            <span class="flex items-center gap-3.5 pr-3.5 capitalize">
              ${team.logo
                ? `<img src="${escapeHtml(team.logo)}" alt="" loading="lazy" class="w-4 h-4 object-contain shrink-0 mr-2">`
                : '<span class="w-4 h-4 shrink-0 mr-2" aria-hidden="true"></span>'}
              <span class="capitalize">${fytTeamNameHtml(team.name, team.override)}</span>
            </span>
          </a>
        </li>
      `).join('') + `<li class="py-4 text-center text-sm text-dove-grey" data-fyt-drawer-empty hidden>${escapeHtml(this.dataset.i18nNoTeams)}</li>`;

      if (search?.value) this.filter(root, search.value);
    }

    filter(root, query) {
      const q = query.toLowerCase().trim();
      const slug = q.replace(/\s+/g, '-');
      let visible = 0;
      root.querySelectorAll('[data-fyt-team]').forEach(li => {
        const match = !q || li.dataset.teamName.includes(q) || li.dataset.teamHandle.includes(slug);
        li.hidden = !match;
        if (match) visible++;
      });
      const empty = root.querySelector('[data-fyt-drawer-empty]');
      if (empty) empty.hidden = visible > 0;
    }
  });
}
